import { classNames } from "../utils";

export interface QualityBadgeProps {
  /** Intrinsic video height in pixels (e.g. 1080, 2160). Null hides the badge. */
  videoHeight: number | null;
  /** Extra classes for positioning (this badge is rendered as a floating pill). */
  className?: string;
}

/**
 * Derives a quality label ("4K" / "HD" / "SD") from the loaded video's
 * intrinsic height and renders a pill badge. Hidden when no metadata is
 * available yet, or for unknown/very small videos.
 *
 * Threshold map matches common YouTube/Netflix conventions:
 *   >= 2160  →  4K
 *   >= 720   →  HD  (covers 720p, 1080p, 1440p)
 *   >= 360   →  SD
 *   <  360   →  no badge (too small to bother labelling)
 */
export function QualityBadge({ videoHeight, className }: QualityBadgeProps) {
  if (videoHeight == null) return null;
  const label =
    videoHeight >= 2160
      ? "4K"
      : videoHeight >= 720
        ? "HD"
        : videoHeight >= 360
          ? "SD"
          : null;
  if (!label) return null;
  return (
    <span
      title={`Video resolution: ${videoHeight}p`}
      className={classNames(
        "pointer-events-none flex h-3.5 w-6 items-center justify-center gap-1 rounded-full border-[0.5px] border-[#DFE1E4] bg-[#F7F8F9] px-1.5 text-[8px] leading-[10px] text-[#282A30]",
        className,
      )}
    >
      {label}
    </span>
  );
}
